import React, { useState, useEffect } from 'react';
import { Search, Phone, Calendar, MapPin, Car, CheckCircle, XCircle, Clock, AlertCircle, ArrowLeft, Navigation, Loader2, CheckCircle2, X, Mail, LogIn } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { formatCurrency, formatDateTime, formatVehicleName } from '../../lib/utils';
import { useAuth } from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const TrackBooking = () => {
  const navigate = useNavigate();
  const { user: authUser, loginWithOtp, logout: authLogout } = useAuth();

  const [phone, setPhone] = useState(authUser?.phone || '');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [error, setError] = useState('');
  const [bookingFilter, setBookingFilter] = useState('active'); // 'active' | 'completed' | 'all'

  // OTP States
  const [otpStep, setOtpStep] = useState(authUser ? 'verified' : 'phone');
  const [otpMethod, setOtpMethod] = useState('email'); // 'phone' (WhatsApp OTP) | 'email'
  const [otpEmail, setOtpEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [otpLoading, setOtpLoading] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpTimer, setOtpTimer] = useState(0);

  // OTP Timer countdown
  useEffect(() => {
    if (otpTimer > 0) {
      const timer = setTimeout(() => setOtpTimer(otpTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpTimer]);

  // Already logged in → skip OTP and show bookings directly.
  // authUser loads asynchronously from the saved session, so this effect
  // flips to the verified view (and fetches) once it's available.
  useEffect(() => {
    if (authUser) {
      setOtpStep('verified');
      if (authUser.phone) setPhone(authUser.phone);
      fetchBookings(); // uses the token → fetches by account identity
    }
  }, [authUser]);

  // Send OTP (phone or email)
  const handleSendOtp = async () => {
    if (otpMethod === 'phone') {
      const cleanPhone = phone.trim().replace(/\D/g, '').slice(-10);
      if (cleanPhone.length < 10) {
        setOtpError('Please enter a valid 10-digit phone number');
        return;
      }
    } else {
      if (!otpEmail || !otpEmail.includes('@')) {
        setOtpError('Please enter a valid email address');
        return;
      }
    }

    setOtpLoading(true);
    setOtpError('');

    try {
      const endpoint = otpMethod === 'phone' ? '/users/send-otp' : '/users/send-email-otp';
      const body = otpMethod === 'phone'
        ? { phone: phone.trim().replace(/\D/g, '').slice(-10) }
        : { email: otpEmail.trim() };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        setOtpStep('otp');
        setOtpTimer(30);
      } else {
        setOtpError(data.message || 'Failed to send OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Verify OTP
  const handleVerifyOtp = async () => {
    const otpCode = otp.join('');
    if (otpCode.length < 6) {
      setOtpError('Please enter the 6-digit OTP');
      return;
    }

    const body = otpMethod === 'phone'
      ? { phone: phone.trim().replace(/\D/g, '').slice(-10), otp: otpCode }
      : { email: otpEmail.trim(), otp: otpCode };

    setOtpLoading(true);
    setOtpError('');

    try {
      const response = await fetch(`${API_URL}/users/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        // Persist session
        loginWithOtp(data.token, data.user);

        const customerPhone = data.user?.phone || phone.trim().replace(/\D/g, '').slice(-10);
        setPhone(customerPhone);
        setOtpStep('verified');
            // Auto-fetch bookings
        fetchBookings(customerPhone);
      } else {
        setOtpError(data.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setOtpError('Something went wrong. Please try again.');
    } finally {
      setOtpLoading(false);
    }
  };

  // Resend OTP
  const handleResendOtp = async () => {
    setOtpLoading(true);
    setOtpError('');

    try {
      const endpoint = otpMethod === 'phone' ? '/users/resend-otp' : '/users/send-email-otp';
      const body = otpMethod === 'phone'
        ? { phone: phone.trim().replace(/\D/g, '').slice(-10) }
        : { email: otpEmail.trim() };

      const response = await fetch(`${API_URL}${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();

      if (data.success) {
        setOtpTimer(30);
        setOtp(['', '', '', '', '', '']);
      } else {
        setOtpError(data.message || 'Failed to resend OTP');
      }
    } catch (err) {
      setOtpError('Something went wrong.');
    } finally {
      setOtpLoading(false);
    }
  };

  // OTP input handlers
  const handleOtpChange = (index, value) => {
    if (value.length > 1) {
      const digits = value.replace(/\D/g, '').slice(0, 6).split('');
      const newOtp = [...otp];
      digits.forEach((d, i) => { if (index + i < 6) newOtp[index + i] = d; });
      setOtp(newOtp);
      const nextIndex = Math.min(index + digits.length, 5);
      document.getElementById(`track-otp-${nextIndex}`)?.focus();
      return;
    }
    if (value && !/^\d$/.test(value)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) {
      document.getElementById(`track-otp-${index + 1}`)?.focus();
    }
  };

  const handleOtpKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      document.getElementById(`track-otp-${index - 1}`)?.focus();
    }
  };

  // Fetch bookings
  const fetchBookings = async (phoneNum) => {
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      // Logged in → fetch by account identity (covers phone AND email accounts).
      // Otherwise fall back to the public phone-based lookup.
      const response = token
        ? await fetch(`${API_URL}/bookings/my-bookings`, {
            headers: { Authorization: `Bearer ${token}` }
          })
        : await fetch(`${API_URL}/bookings/my-bookings/${phoneNum || phone.trim()}`);
      const data = await response.json();

      if (data.success) {
        setBookings(data.bookings || []);
      } else {
        setError(data.message || 'Failed to fetch bookings');
      }
    } catch (err) {
      setError('Failed to fetch bookings. Please try again.');
    } finally {
      setLoading(false);
      setSearched(true);
    }
  };

  const handleLogout = () => {
    authLogout();
    setOtpStep('phone');
    setPhone('');
    setBookings([]);
    setSearched(false);
    setOtp(['', '', '', '', '', '']);
    setOtpError('');
  };

  const getStatusConfig = (status) => {
    const configs = {
      pending: {
        color: 'bg-amber-100 text-amber-800 border-amber-200',
        icon: Clock,
        label: 'Awaiting Confirmation',
        description: 'Your booking is being reviewed by the owner'
      },
      confirmed: {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: CheckCircle,
        label: 'Confirmed',
        description: 'Your booking has been confirmed'
      },
      active: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: Car,
        label: 'Active',
        description: 'Your trip is currently in progress'
      },
      completed: {
        color: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: CheckCircle,
        label: 'Completed',
        description: 'Your trip has been completed'
      },
      cancelled: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: 'Cancelled',
        description: 'This booking has been cancelled'
      }
    };
    return configs[status] || configs.pending;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/browse" className="flex items-center text-gray-600 hover:text-gray-900">
              <ArrowLeft className="h-5 w-5 mr-2" />
              <span className="text-sm">Back to Browse</span>
            </Link>
            <h1 className="text-lg font-bold text-gray-900">Track Booking</h1>
            {authUser ? (
              <button
                onClick={handleLogout}
                className="flex items-center gap-1.5 text-sm text-red-600 hover:text-red-700 font-medium"
              >
                <LogIn className="h-4 w-4" /> Logout
              </button>
            ) : (
              <div className="w-24" />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* OTP Verification Section */}
        {otpStep !== 'verified' && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            {otpStep === 'phone' && (
              <>
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
                    {otpMethod === 'phone' ? <Phone className="h-8 w-8 text-blue-600" /> : <Mail className="h-8 w-8 text-blue-600" />}
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Track Your Booking</h2>
                  <p className="text-gray-600 mt-1">Verify your identity to view your bookings</p>
                </div>

                <div className="max-w-md mx-auto space-y-4">
                  {/* Verification method — WhatsApp (phone) or Email */}
                  <div className="grid grid-cols-2 gap-2 p-1 bg-gray-100 rounded-xl">
                    <button
                      type="button"
                      onClick={() => { setOtpMethod('phone'); setOtpError(''); }}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${otpMethod === 'phone' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <Phone className="h-4 w-4" /> WhatsApp
                    </button>
                    <button
                      type="button"
                      onClick={() => { setOtpMethod('email'); setOtpError(''); }}
                      className={`flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-colors ${otpMethod === 'email' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                    >
                      <Mail className="h-4 w-4" /> Email
                    </button>
                  </div>

                  {/* Phone Input — OTP via WhatsApp */}
                  {otpMethod === 'phone' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm font-medium">+91</span>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                        placeholder="Enter 10-digit mobile number"
                        maxLength={10}
                        className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg tracking-wider"
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">We'll send your OTP on WhatsApp</p>
                  </div>
                  )}

                  {/* Email Input — OTP via email */}
                  {otpMethod === 'email' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email Address</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                      <input
                        type="email"
                        value={otpEmail}
                        onChange={(e) => setOtpEmail(e.target.value)}
                        placeholder="Enter your email address"
                        className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                  )}

                  {otpError && (
                    <p className="text-sm text-red-600 flex items-center gap-1">
                      <X className="h-4 w-4" /> {otpError}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={handleSendOtp}
                    disabled={otpLoading || (otpMethod === 'phone' ? phone.replace(/\D/g, '').length < 10 : (!otpEmail || !otpEmail.includes('@')))}
                    className="w-full py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
                  >
                    {otpLoading ? (
                      <><Loader2 className="h-5 w-5 animate-spin" /> Sending OTP...</>
                    ) : (
                      'Send OTP'
                    )}
                  </button>
                </div>
              </>
            )}

            {otpStep === 'otp' && (
              <div className="max-w-md mx-auto">
                <div className="text-center mb-6">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  </div>
                  <h2 className="text-xl font-bold text-gray-900">Enter OTP</h2>
                  <p className="text-gray-600 mt-1">
                    OTP sent to <span className="font-semibold text-gray-900">
                      {otpMethod === 'phone' ? `+91 ${phone}` : otpEmail}
                    </span>
                  </p>
                  <button
                    type="button"
                    onClick={() => { setOtpStep('phone'); setOtp(['', '', '', '', '', '']); setOtpError(''); }}
                    className="text-xs text-blue-600 hover:underline mt-1"
                  >
                    Change {otpMethod === 'phone' ? 'number' : 'email'}
                  </button>
                </div>


                {/* OTP Input Boxes */}
                <div className="flex justify-center gap-3 mb-4">
                  {otp.map((digit, idx) => (
                    <input
                      key={idx}
                      id={`track-otp-${idx}`}
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      value={digit}
                      onChange={(e) => handleOtpChange(idx, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(idx, e)}
                      className="w-11 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
                      autoFocus={idx === 0}
                    />
                  ))}
                </div>

                {otpError && (
                  <p className="text-sm text-red-600 text-center flex items-center justify-center gap-1 mb-4">
                    <X className="h-4 w-4" /> {otpError}
                  </p>
                )}

                <button
                  type="button"
                  onClick={handleVerifyOtp}
                  disabled={otpLoading || otp.join('').length < 6}
                  className="w-full py-3 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2 mb-3"
                >
                  {otpLoading ? (
                    <><Loader2 className="h-5 w-5 animate-spin" /> Verifying...</>
                  ) : (
                    <><CheckCircle2 className="h-5 w-5" /> Verify & View Bookings</>
                  )}
                </button>

                {/* Resend Timer */}
                <div className="text-center">
                  {otpTimer > 0 ? (
                    <p className="text-sm text-gray-500">Resend OTP in <span className="font-semibold text-gray-700">{otpTimer}s</span></p>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResendOtp}
                      disabled={otpLoading}
                      className="text-sm text-blue-600 hover:text-blue-700 font-medium hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Verified Badge + Results */}
        {otpStep === 'verified' && (
          <>
            <div className="bg-white rounded-2xl shadow-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-sm text-green-800">
                    Welcome, <strong>{authUser?.name || `+91 ${phone}`}</strong>
                  </span>
                </div>
                <button
                  onClick={() => fetchBookings()}
                  disabled={loading}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                  Refresh
                </button>
              </div>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center text-red-700">
                <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {loading ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-gray-600">Loading your bookings...</p>
              </div>
            ) : bookings.length === 0 ? (
              <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-full mb-4">
                  <Calendar className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bookings Found</h3>
                <p className="text-gray-600">No bookings found for this account.</p>
                <Link
                  to="/browse"
                  className="inline-flex items-center mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Car className="h-4 w-4 mr-2" />
                  Browse Vehicles
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Filter Tabs */}
                {(() => {
                  const activeCount = bookings.filter(b => ['pending', 'confirmed', 'active'].includes(b.status)).length;
                  const completedCount = bookings.filter(b => ['completed', 'cancelled'].includes(b.status)).length;
                  return (
                    <div className="flex items-center gap-2 bg-white rounded-xl p-1.5 shadow-sm">
                      <button
                        onClick={() => setBookingFilter('active')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          bookingFilter === 'active'
                            ? 'bg-blue-600 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Active ({activeCount})
                      </button>
                      <button
                        onClick={() => setBookingFilter('completed')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          bookingFilter === 'completed'
                            ? 'bg-gray-700 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        Past ({completedCount})
                      </button>
                      <button
                        onClick={() => setBookingFilter('all')}
                        className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${
                          bookingFilter === 'all'
                            ? 'bg-gray-900 text-white shadow-sm'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        All ({bookings.length})
                      </button>
                    </div>
                  );
                })()}

                {/* Filtered Bookings */}
                {(() => {
                  const filtered = bookingFilter === 'active'
                    ? bookings.filter(b => ['pending', 'confirmed', 'active'].includes(b.status))
                    : bookingFilter === 'completed'
                    ? bookings.filter(b => ['completed', 'cancelled'].includes(b.status))
                    : bookings;

                  if (filtered.length === 0) {
                    return (
                      <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-100 rounded-full mb-3">
                          {bookingFilter === 'active'
                            ? <Clock className="h-7 w-7 text-gray-400" />
                            : <CheckCircle className="h-7 w-7 text-gray-400" />
                          }
                        </div>
                        <p className="text-gray-600 text-sm">
                          {bookingFilter === 'active'
                            ? 'No active bookings right now.'
                            : 'No past bookings found.'
                          }
                        </p>
                      </div>
                    );
                  }

                  return filtered.map((booking) => {
                  const statusConfig = getStatusConfig(booking.status);
                  const StatusIcon = statusConfig.icon;

                  return (
                    <div key={booking._id} className="bg-white rounded-2xl shadow-lg overflow-hidden">
                      {/* Status Header */}
                      <div className={`px-6 py-4 ${statusConfig.color} border-b`}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <StatusIcon className="h-5 w-5 mr-2" />
                            <span className="font-semibold">{statusConfig.label}</span>
                          </div>
                          <span className="text-sm">#{booking._id.slice(-6)}</span>
                        </div>
                        <p className="text-sm mt-1 opacity-80">{statusConfig.description}</p>
                      </div>

                      {/* Rejection Reason Banner */}
                      {booking.status === 'cancelled' && booking.rejectionReason && (
                        <div className="bg-red-50 border-b border-red-100 px-6 py-4">
                          <div className="flex items-start">
                            <XCircle className="h-5 w-5 text-red-500 mr-3 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="font-medium text-red-800">Reason for Cancellation</p>
                              <p className="text-red-700 mt-1">{booking.rejectionReason}</p>
                              {booking.rejectedAt && (
                                <p className="text-red-600 text-xs mt-2">
                                  Cancelled on {formatDateTime(booking.rejectedAt)}
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Booking Details */}
                      <div className="p-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Car Info */}
                          <div className="flex items-start">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                              <Car className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Vehicle</p>
                              <p className="font-semibold text-gray-900">
                                {formatVehicleName(booking.carId, { showYear: false })}
                              </p>
                              <p className="text-xs text-gray-500">
                                {booking.carId?.registrationNumber}
                              </p>
                            </div>
                          </div>

                          {/* Date Info */}
                          <div className="flex items-start">
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                              <Calendar className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Trip Date</p>
                              <p className="font-semibold text-gray-900">
                                {formatDateTime(booking.startDate)}
                              </p>
                              {booking.endDate !== booking.startDate && (
                                <p className="text-xs text-gray-500">
                                  to {formatDateTime(booking.endDate)}
                                </p>
                              )}
                            </div>
                          </div>

                          {/* Pickup Location */}
                          {booking.pickupLocation && (
                            <div className="flex items-start">
                              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-3">
                                <MapPin className="h-5 w-5 text-green-600" />
                              </div>
                              <div>
                                <p className="text-sm text-gray-500">Pickup</p>
                                <p className="font-semibold text-gray-900">{booking.pickupLocation}</p>
                              </div>
                            </div>
                          )}

                          {/* Total Cost */}
                          <div className="flex items-start">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                              <span className="text-blue-600 font-bold">₹</span>
                            </div>
                            <div>
                              <p className="text-sm text-gray-500">Total Amount</p>
                              <p className="font-bold text-blue-600 text-lg">
                                {formatCurrency(booking.totalCost || 0)}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Track Driver - Show for active bookings */}
                        {booking.status === 'active' && booking.trackingCode && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between bg-blue-50 rounded-lg p-3">
                              <div className="flex items-center">
                                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                                  <Navigation className="h-5 w-5 text-blue-600" />
                                </div>
                                <div>
                                  <p className="font-medium text-blue-800">Driver is on the way!</p>
                                  <p className="text-sm text-blue-600">Track live location</p>
                                </div>
                              </div>
                              <Link
                                to={`/track/${booking.trackingCode}`}
                                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all"
                              >
                                <MapPin className="h-4 w-4 mr-2" />
                                Track
                              </Link>
                            </div>
                          </div>
                        )}

                        {/* Owner Contact */}
                        {booking.ownerId && booking.status !== 'cancelled' && (
                          <div className="mt-4 pt-4 border-t">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm text-gray-500">Contact Owner</p>
                                <p className="font-medium text-gray-900">
                                  {booking.ownerId.travelsInfo?.name || booking.ownerId.name}
                                </p>
                              </div>
                              <a
                                href={`tel:${booking.ownerId.phone || booking.ownerId.travelsInfo?.phone}`}
                                className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all"
                              >
                                <Phone className="h-4 w-4 mr-2" />
                                Call
                              </a>
                            </div>
                          </div>
                        )}

                        {/* Info text for Cancelled */}
                        {booking.status === 'cancelled' && (
                          <div className="mt-4 pt-4 border-t text-center">
                            <p className="text-sm text-gray-500">
                              This booking has been cancelled. You can place a new booking now.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                });
                })()}
              </div>
            )}
          </>
        )}

        {/* Help Section - Only show before verification */}
        {otpStep === 'phone' && (
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-6 mt-6">
            <h3 className="font-semibold text-blue-900 mb-2">Need Help?</h3>
            <p className="text-blue-800 text-sm mb-4">
              Enter the phone number or email you used while making the booking. We'll send an OTP to verify your identity before showing your bookings.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div className="flex items-center text-blue-700">
                <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                <span>View booking status</span>
              </div>
              <div className="flex items-center text-blue-700">
                <Phone className="h-4 w-4 mr-2 text-blue-600" />
                <span>Contact owner directly</span>
              </div>
              <div className="flex items-center text-blue-700">
                <Calendar className="h-4 w-4 mr-2 text-purple-600" />
                <span>See trip details</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TrackBooking;
